"use client";

import {
  appliedFilterIdsOf,
  catalogIdsOf,
  defaultEnabledIdsOf,
  emptyValueForFilter,
  syncEnabledFilterIds,
  withAppliedEnabledIds,
  type FilterFieldValue,
  type FilterVisibilityCatalogItem,
} from "@/lib/utils/filter-visibility";
import {
  readFilterVisibility,
  writeFilterVisibility,
} from "@/lib/utils/table-query-persistence";
import { useCallback, useEffect, useRef, useState } from "react";

export interface FilterVisibilityPersistence {
  /**
   * When greater than 1, chips are stored at `{key}-v{revision}-filter-visibility`.
   * A missing revision key is filled once from the legacy `{key}-filter-visibility` blob.
   */
  revision?: number;
  /** Replace a legacy id list, or return null to copy it unchanged. */
  migrateStoredIds?: (stored: readonly string[]) => readonly string[] | null;
}

function resolveStoredIds(
  storageKey: string,
  persistence: FilterVisibilityPersistence | undefined,
): { ids: string[] | null; publish: boolean } {
  const revision = persistence?.revision ?? 1;
  if (revision <= 1) {
    return { ids: readFilterVisibility(storageKey), publish: false };
  }

  const current = readFilterVisibility(storageKey, revision);
  if (current) return { ids: current, publish: false };

  const legacy = readFilterVisibility(storageKey);
  if (!legacy) return { ids: null, publish: false };

  const migrated = persistence?.migrateStoredIds?.(legacy) ?? legacy;
  return { ids: [...migrated], publish: true };
}

function initialEnabledIds(
  catalog: ReadonlyArray<FilterVisibilityCatalogItem> | undefined,
  filterValues: Record<string, FilterFieldValue>,
  storageKey: string | undefined,
  persistence: FilterVisibilityPersistence | undefined,
): { ids: string[]; fromStorage: boolean; publish: boolean } {
  const applied = appliedFilterIdsOf(catalog, filterValues);
  const resolved = storageKey
    ? resolveStoredIds(storageKey, persistence)
    : { ids: null, publish: false };
  const base = resolved.ids ?? defaultEnabledIdsOf(catalog);
  return {
    ids: [...withAppliedEnabledIds(base, applied)],
    fromStorage: resolved.ids !== null,
    publish: resolved.publish,
  };
}

export function useFilterVisibility(
  catalog: ReadonlyArray<FilterVisibilityCatalogItem> | undefined,
  filterValues: Record<string, FilterFieldValue>,
  onFiltersChange?: (values: Record<string, FilterFieldValue>) => void,
  storageKey?: string,
  persistence?: FilterVisibilityPersistence,
): {
  enabledFilterIds: string[];
  handleToggleFilterVisibility: (_filterId: string, _enabled: boolean) => void;
  handleSetAllFilterVisibility: (_enabled: boolean) => void;
} {
  const revision = persistence?.revision ?? 1;
  const initial = initialEnabledIds(
    catalog,
    filterValues,
    storageKey,
    persistence,
  );
  const [enabledFilterIds, setEnabledFilterIds] = useState<string[]>(
    () => initial.ids,
  );
  const [committedKey, setCommittedKey] = useState(storageKey ?? "");
  const prevCatalogIdsRef = useRef<string[]>(catalogIdsOf(catalog));
  const hydratedFromStorageRef = useRef(initial.fromStorage);
  const persistReadyForKey = useRef<string | null>(null);
  const publishRevisionRef = useRef(initial.publish);
  const appliedKey = appliedFilterIdsOf(catalog, filterValues).join("\0");

  if ((storageKey ?? "") !== committedKey) {
    const next = initialEnabledIds(
      catalog,
      filterValues,
      storageKey,
      persistence,
    );
    setCommittedKey(storageKey ?? "");
    setEnabledFilterIds(next.ids);
    prevCatalogIdsRef.current = catalogIdsOf(catalog);
    hydratedFromStorageRef.current = next.fromStorage;
    persistReadyForKey.current = null;
    publishRevisionRef.current = next.publish;
  }

  useEffect(() => {
    const nextIds = catalogIdsOf(catalog);
    if (nextIds.length === 0) return;

    const prevCatalog = prevCatalogIdsRef.current;
    prevCatalogIdsRef.current = nextIds;
    const appliedIds = appliedKey === "" ? [] : appliedKey.split("\0");
    const syncPrevCatalog =
      prevCatalog.length === 0 && hydratedFromStorageRef.current
        ? nextIds
        : prevCatalog;

    setEnabledFilterIds((prev) => {
      const synced = syncEnabledFilterIds(prev, syncPrevCatalog, catalog ?? []);
      const next = withAppliedEnabledIds(synced, appliedIds);
      if (
        prev.length === next.length &&
        prev.every((id, index) => id === next[index])
      ) {
        return prev;
      }
      return [...next];
    });
  }, [catalog, appliedKey]);

  useEffect(() => {
    if (!storageKey) return;
    if (publishRevisionRef.current && revision > 1) {
      writeFilterVisibility(storageKey, enabledFilterIds, revision);
      publishRevisionRef.current = false;
      persistReadyForKey.current = storageKey;
      return;
    }
    if (persistReadyForKey.current !== storageKey) {
      persistReadyForKey.current = storageKey;
      return;
    }
    writeFilterVisibility(storageKey, enabledFilterIds, revision);
  }, [enabledFilterIds, revision, storageKey]);

  const handleToggleFilterVisibility = useCallback(
    (filterId: string, enabled: boolean) => {
      setEnabledFilterIds((prev) => {
        const isOn = prev.includes(filterId);
        if (enabled) {
          return isOn ? prev : [...prev, filterId];
        }
        return isOn ? prev.filter((id) => id !== filterId) : prev;
      });

      if (!enabled && catalog && onFiltersChange) {
        const filter = catalog.find((item) => item.id === filterId);
        if (filter) {
          onFiltersChange({
            ...filterValues,
            [filterId]: emptyValueForFilter(filter),
          });
        }
      }
    },
    [catalog, filterValues, onFiltersChange],
  );

  const handleSetAllFilterVisibility = useCallback(
    (enabled: boolean) => {
      const allIds = catalogIdsOf(catalog);
      if (enabled) {
        setEnabledFilterIds(allIds);
        return;
      }

      setEnabledFilterIds([]);
      if (!catalog || !onFiltersChange || allIds.length === 0) return;

      const next = { ...filterValues };
      for (const filter of catalog) {
        next[filter.id] = emptyValueForFilter(filter);
      }
      onFiltersChange(next);
    },
    [catalog, filterValues, onFiltersChange],
  );

  return {
    enabledFilterIds,
    handleToggleFilterVisibility,
    handleSetAllFilterVisibility,
  };
}
